/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {StateDeclarationBuilder, AdnRouterHelperApi} from '../../routing/state-utils';
import {ApiKeyApiService, ApiKeyModel} from './api-key-api.service';
import {ApiKeyEditPageComponent} from './api-key-edit-page.component';
import {ApiKeyRoutePageComponent} from './api-key-route-page.component';
import {isOAuthConsentRequest, OAUTH_CONSENT_PARAMS} from './api-key-commons';

function oauthConsentParams(params: any): Record<string, any> {
  return {
    app: params.app,
    callback: params.callback,
    code_challenge: params.code_challenge,
    code_challenge_method: params.code_challenge_method,
    redirect_uri: params.redirect_uri,
    state: params.state,
    client_id: params.client_id,
    resource: params.resource
  };
}

export function apiKeyStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(ApiKeyApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const readOnly = () => permissions.isSelfServiceOwn();
  const rh = adnRouterHelper.createRouterHelper('api-keys.api-key', ApiKeyApiService, 'app.adm', 'app/admin');
  const newItem = rh.newItem(OAUTH_CONSENT_PARAMS);
  const defaultNewItemEnter = newItem.onEnter;

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: ApiKeyRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), Object.assign(newItem, {
    url: '/new?app&callback&code_challenge&code_challenge_method&redirect_uri&state&client_id&resource',
    onEnter: function($transition$: any) {
      const params = $transition$.params('to');
      if (isOAuthConsentRequest(params)) {
        return adnRouterHelper.services.state().target('nude.mcp-authorize', oauthConsentParams(params));
      }
      return defaultNewItemEnter($transition$);
    }
  }));
  states.add(rh.itemPath(), rh.item({
    url: '/api-key/:id?app&callback&code_challenge&code_challenge_method&redirect_uri&state&client_id&resource',
    params: {
      app: null, callback: null,
      code_challenge: null, code_challenge_method: null, redirect_uri: null, state: null, client_id: null, resource: null
    },
    resolve: {
      apiKeyPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: ApiKeyModel = {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []};
          return new SignalAdminResourcePageState(model, true, readOnly(), api, 'API Key');
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'API Key'));
      },
      model: function(apiKeyPage: SignalAdminResourcePageState<ApiKeyModel>) {
        return apiKeyPage.model();
      }
    },
    views: {
      'content@app': {component: ApiKeyEditPageComponent, bindings: {state: 'apiKeyPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'apiKeyPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'apiKeyPage'}}
    }
  }));
  return states.build();
}
